import { useState } from 'react';

import type { Booking, BookingGarment, BookingWearer } from '@/api/bookings';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice } from '@/components/PageHeading';
import { GarmentImage } from '@/components/GarmentImage';
import { StatusBadge } from '@/components/StatusBadge';
import { ConditionPhotos } from './ConditionPhotos';
import { ScanBox } from './ScanBox';
import { formatAmount } from '@core/format/money';
import { MoneyLine, MoneyLines } from './MoneyLine';
import {
  DepositRefundPointValues,
  type DepositRefundPoint,
  type PaymentMethod,
} from '@/api/bookings';
import { RefundMethodChips } from './RefundMethodChips';
import { useBookingPaymentAccounts } from '@/api/bookings';
import { AccountChoice } from '@/components/AccountChoice';
import { CounterBand } from './CounterBand';
import { refundPointLabel } from '@core/bookings/paymentWords';
import { formatDate } from '@core/format/dates';
import {
  accessoriesBack,
  accessoriesIssued,
  forfeitedAmount,
  lateFeeIsTakenNow,
  settleBooking,
  takeBackLabel,
} from '@core/bookings/settlement';
import { isOutOnRent } from '@core/bookings/tones';
import { withReference } from '@core/units/referenceNumber';

/**
 * Taking garments back, and settling the deposit.
 *
 * <p>
 * <strong>The highest-dispute moment in the business</strong>, which is why the arithmetic is shown
 * as arithmetic rather than presented as a final number. Every deduction is named and attributed;
 * a customer standing at the counter with a garment they believe is undamaged deserves to see the
 * working, and a shop that shows it wins the argument far more often.
 * </p>
 *
 * <p>
 * The refund timing is a control rather than a fixed step, because both ends of the deposit's life
 * are configurable and all six combinations are somebody's shop. Refunding at the counter is the
 * fast option and it has a cost the screen states out loud: closing the deposit before cleaning has
 * seen the garments makes anything found later an unsecured recoverable.
 * </p>
 */
export function ReturnCounter({
  booking,
  onCancel,
  onSettle,
  busy = false,
  currency = '',
}: {
  booking: Booking;
  onCancel: () => void;
  onSettle: (input: {
    garmentIds: number[];
    refundPoint: DepositRefundPoint;
    lateFee: number | null;
    accessoryIds: number[];
    refundMethod: PaymentMethod;
    /**
     * The account the refund leaves, or what is collected goes into; null is the method's default.
     */
    cashAccountId: number | null;
    /** Take what a deposit closed here does not cover, by the same method, in the same click. */
    collect: boolean;
  }) => void;
  busy?: boolean;
  currency?: string;
}) {
  // Every garment on the booking, out or not, so the scan box can tell 'never went out' from 'not
  // on this booking' — two different mistakes wanting two different sentences.
  const everyGarment = booking.wearers.flatMap((wearer) => wearer.garments);
  const outstanding = everyGarment.filter((garment) => isOutOnRent(garment.state));

  const [received, setReceived] = useState<Set<number>>(
    () => new Set(outstanding.map((garment) => garment.id)),
  );

  // Everything that went out starts ticked as back. The generous default is deliberate: an unticked
  // accessory becomes a deduction on the customer's settlement, so it has to be a clerk's deliberate
  // act rather than the consequence of a screen nobody scrolled.
  const [accessories, setAccessories] = useState<Set<number>>(
    () => new Set(accessoriesIssued(booking).map((accessory) => accessory.id)),
  );

  const [refundPoint, setRefundPoint] = useState<DepositRefundPoint>(booking.deposit.refundPoint);

  // Cash is the counter's default because it is what a counter does; the point of the control is
  // that the other four are sayable. Recorded as cash, a refund handed back over UPI takes money
  // out of a drawer it was never in — see `RefundMethodChips`.
  const [refundMethod, setRefundMethod] = useState<PaymentMethod>('Cash');
  // Which account the refund leaves, or what is collected goes into (split payments); null is the
  // method's default.
  const [account, setAccount] = useState<number | null>(null);
  const accounts = useBookingPaymentAccounts(booking.branchId ?? undefined);

  /*
     The opening figure comes off the booking, which is the server summing each garment still out at
     the extension rate it was booked at, past the shop's grace period.

     It used to be a prop this screen's only caller never passed, defaulting to zero — so a late fee
     was charged exactly when somebody remembered to type one, and the two tenant settings meant to
     drive it were read by no code at all. Taken from the booking rather than reinstated as a prop
     because the booking is already here: a second channel for one number is how the field and the
     banner above it come to disagree.

     Still local state, and still editable: the deduction list and the refund figure move as the
     operator types, which is the whole point of showing the settlement line by line.
  */
  const [charged, setCharged] = useState(booking.suggestedLateFee ?? 0);

  // On an overdue return the late fee is the first control and the first decision, which is the
  // right place to be standing. Otherwise it is the first garment's tick box.
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  const partial = received.size < outstanding.length;

  // Whether this call is the one that takes the fee — the server's rule, so the field below is
  // offered only where it does something. See `lateFeeIsTakenNow`.
  const takesLateFee = lateFeeIsTakenNow({ partial, refundPoint });

  // The clerk's live ticks, not the stored state — the arithmetic below has to move as a missing
  // kalgi is unticked, or the counter shows a settlement that ignores the decision being made on it.
  //
  // Zero where the fee is not taken now, so the deposit panel cannot show a deduction this call
  // will not make: showing one is how the screen came to promise money it then threw away.
  const settlement = settleBooking(booking, takesLateFee ? charged : 0, accessories);
  const forfeited = forfeitedAmount(settlement);
  // The same click takes what the deposit does not cover.
  const collectsHere = takesLateFee && settlement.toCollect > 0;

  return (
    // Enter moves to the next control and the last one hands focus to Settle — see
    // `enterKeyNavigation`. The wrapper exists for that handler: the commit button lives in the
    // heading's action slot, so a handler on the grid below would have nothing to hand the last
    // field to.
    <div ref={fields} {...enterKeyScope}>
      <CounterBand
        eyebrow={`${booking.documentNumber} · ${booking.customerName}${
          booking.isOverdue ? ` · ${booking.daysOverdue} days late` : ''
        }`}
        title="Take back"
        onBack={onCancel}
        trailing={
          <span className="text-[13px] text-white/85">
            Due back {formatDate(booking.possessionToDate)}
          </span>
        }
      />

      <div className="flex flex-col gap-3 lg:gap-4">
        {booking.isOverdue && (
          <Notice tone="warning" title={`Late return · ${booking.daysOverdue} days`}>
            <p>
              Downstream bookings on these garments may be affected. Check the cascade before
              promising anything to the next customer.
            </p>

            {/*
              The field is offered only when this call actually takes the fee.

              A late fee funds a deduction against the deposit and lives nowhere else, so on a
              partial return — or where this shop refunds after inspection or after cleaning — the
              server has nothing to apply it to. It used to accept the figure and drop it: the panel
              showed 9,600 coming off the deposit, the settle succeeded, and the money existed
              nowhere afterwards. `lateFeeIsTakenNow` is the server's own rule, so the counter
              never offers a write the API refuses.
            */}
            {takesLateFee ? (
              <label className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-[12.5px] font-medium text-gray-900">Late fee to charge</span>
                <input
                  type="number"
                  onWheel={(event) => event.currentTarget.blur()}
                  min={0}
                  value={charged}
                  disabled={busy}
                  onChange={(event) => setCharged(Math.max(0, Number(event.target.value) || 0))}
                  className="border-border-color h-8 w-28 rounded border bg-white px-2.5 text-[13px]"
                />
                <span className="text-default text-[11.5px]">
                  {/*
                    Says where the figure came from without restating a day count. The chargeable
                    days are `daysOverdue` minus the shop's grace period, and the grace period is not
                    sent here — printing the raw overdue count beside a fee computed from fewer days
                    would be showing working that does not add up, which is worse than showing none.
                  */}
                  {booking.suggestedLateFee !== null
                    ? "Suggested from this booking's extension rate and the shop's grace period. "
                    : 'No rate prices a late day here, so nothing is suggested. '}
                  Deducted from the deposit below. Leave at zero to waive it.
                </span>
              </label>
            ) : (
              /*
                Not silence: the shop is still owed the money, and the clerk has to be told where it
                is charged instead rather than left to assume this screen took it. The suggestion is
                still printed, because it is the figure they will need when they get there.
              */
              <p className="text-default mt-2 text-[11.5px]">
                {booking.suggestedLateFee !== null && (
                  <>
                    A late fee of {currency}
                    {formatAmount(booking.suggestedLateFee)} is suggested for these days.{' '}
                  </>
                )}
                It comes out of the deposit, so it is charged when the deposit is settled —{' '}
                {partial
                  ? 'once the rest of the garments are back.'
                  : 'on this booking that happens after inspection rather than here.'}
              </p>
            )}
          </Notice>
        )}

        <div className="grid gap-3 lg:grid-cols-[1fr_320px] lg:gap-4">
          <Card flush>
            <div className="border-border-color flex flex-wrap items-start justify-between gap-2 border-b px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">Scan each garment in</p>
                <span className="text-default text-[12.5px]">
                  {received.size} of {outstanding.length} back
                </span>
              </div>

              {/* Everything that went out starts ticked as back — see below — so scanning here is
                  confirmation rather than selection. It still earns its place: a tag that is not on
                  this booking is refused by name, which is the check a clerk cannot do by eye
                  across two bookings hanging on the same rail. */}
              <ScanBox
                garments={everyGarment}
                eligible={(garment) => isOutOnRent(garment.state)}
                disabled={busy}
                placeholder="Scan a garment in"
                ineligibleHint={(garment) =>
                  `${garment.itemName} is on this booking but never went out.`
                }
                onMatch={(garment) => setReceived((was) => new Set(was).add(garment.id))}
              />
            </div>

            <div className="divide-border-color divide-y">
              {booking.wearers.map((wearer) => (
                <WearerBlock
                  key={wearer.id}
                  wearer={wearer}
                  received={received}
                  currency={currency}
                  bookingId={booking.id}
                  accessories={accessories}
                  onToggleAccessory={(id) =>
                    setAccessories((was) => {
                      const next = new Set(was);

                      if (next.has(id)) {
                        next.delete(id);
                      } else {
                        next.add(id);
                      }

                      return next;
                    })
                  }
                  onToggle={(id) =>
                    setReceived((was) => {
                      const next = new Set(was);

                      if (next.has(id)) {
                        next.delete(id);
                      } else {
                        next.add(id);
                      }

                      return next;
                    })
                  }
                />
              ))}
            </div>
          </Card>

          <div className="flex flex-col gap-3 lg:gap-4">
            <Card>
              <p className="mb-2.5 text-sm font-semibold text-gray-900">Deposit settlement</p>

              <RefundTiming value={refundPoint} onChange={setRefundPoint} disabled={busy} />

              {refundPoint === 'AtCounter' && (
                <div className="mt-3">
                  <Notice tone="warning" title="Refunding before inspection">
                    This closes the deposit before cleaning has seen the garments. Anything found
                    afterwards is an unsecured recoverable — money owed with nothing left to take it
                    from.
                  </Notice>
                </div>
              )}

              {/* Only where this call actually writes a refund row: the other two refund points
              leave the deposit open for a later settlement to close, and a refund of nothing
              moves no money whichever way the clerk says it moved. */}
              {refundPoint === 'AtCounter' &&
                (settlement.refund > 0 || settlement.toCollect > 0) && (
                  <div className="mt-3">
                    <RefundMethodChips
                      value={refundMethod}
                      onChange={(method) => {
                        setRefundMethod(method);
                        setAccount(null);
                      }}
                      disabled={busy}
                    />
                    {(settlement.refund > 0 || collectsHere) && (
                      <div className="mt-2">
                        <AccountChoice
                          label={collectsHere ? 'Into which account?' : 'Out of which account?'}
                          method={refundMethod}
                          accounts={accounts.data}
                          value={account}
                          onChange={setAccount}
                          disabled={busy}
                        />
                      </div>
                    )}
                  </div>
                )}

              <MoneyLines className="mt-3">
                <MoneyLine
                  label="Deposit held"
                  value={`${currency}${formatAmount(settlement.depositHeld)}`}
                  tone="brand"
                />
                {settlement.deductions.map((deduction, index) => (
                  <MoneyLine
                    // Deductions have no id of their own; they are derived, and their position is
                    // stable for a given booking.
                    key={`${deduction.label}-${index}`}
                    label={deduction.label}
                    hint={deduction.wearerName}
                    value={`− ${currency}${formatAmount(deduction.amount)}`}
                    tone="danger"
                  />
                ))}
                {/* What is left of the deposit paying the rent still owed (owner, 2026-10-14). */}
                {settlement.towardsBalance > 0 && (
                  <MoneyLine
                    label="Rent still owed"
                    hint="Paid from the deposit"
                    value={`− ${currency}${formatAmount(settlement.towardsBalance)}`}
                    tone="warning"
                  />
                )}
                <MoneyLine
                  label="Refund to customer"
                  value={`${currency}${formatAmount(settlement.refund)}`}
                  total
                  tone="success"
                />
                {takesLateFee && settlement.toCollect > 0 && (
                  <MoneyLine
                    label="Still to take"
                    value={`${currency}${formatAmount(settlement.toCollect)}`}
                    total
                    tone="warning"
                  />
                )}
              </MoneyLines>

              {settlement.shortfall > 0 && (
                <div className="mt-3">
                  <Notice
                    tone="danger"
                    title={`${currency}${formatAmount(settlement.shortfall)} beyond the deposit`}
                  >
                    Deductions exceed what the shop is holding. This is owed by the customer and is
                    not secured by anything.
                  </Notice>
                </div>
              )}

              <Button
                variant="primary"
                data-enter-key-commit
                shortcut="save"
                loading={busy}
                disabled={received.size === 0 || busy}
                onClick={() =>
                  onSettle({
                    garmentIds: [...received],
                    refundPoint,
                    // Never sent where the server would refuse it — see `lateFeeIsTakenNow`.
                    lateFee: takesLateFee && charged > 0 ? charged : null,
                    accessoryIds: accessoriesBack(booking, received, accessories),
                    refundMethod,
                    cashAccountId: account,
                    collect: collectsHere,
                  })
                }
                className="mt-4 h-12 w-full justify-center text-[15px]"
              >
                {takeBackLabel(
                  {
                    received: received.size,
                    outstanding: outstanding.length,
                    refundPoint,
                    refund: settlement.refund,
                    toCollect: settlement.toCollect,
                  },
                  (amount) => `${currency}${formatAmount(amount)}`,
                )}
              </Button>

              {forfeited > 0 && (
                <p className="text-default mt-3 text-[11.5px] leading-relaxed">
                  {/* Held money is not a supply; money kept for damage is, and §25 taxes it. */}
                  Charges include GST — {currency}
                  {formatAmount(forfeited)} kept for them is taxed on the settlement document. Rent
                  paid from the deposit is not taxed again.
                </p>
              )}
            </Card>

            <Card>
              <p className="mb-2 text-sm font-semibold text-gray-900">On settle</p>
              <ul className="text-default space-y-1 text-[12.5px]">
                <li>Cleaning jobs raised for every garment back</li>
                <li>Repairable damage routed to alteration</li>
                <li>Inspection note and deposit refund note issued</li>
                <li>Customer risk score updated from what happened</li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * When the deposit goes back — one line from the booking's own refund point with Change, as on the
 * phone (2026-10-22), rather than three choices on every return. Defaults to the shop's setting and
 * is overridable per booking, because a counter sometimes knows something the setting does not.
 */
function RefundTiming({
  value,
  onChange,
  disabled = false,
}: {
  value: DepositRefundPoint;
  onChange: (next: DepositRefundPoint) => void;
  disabled?: boolean;
}) {
  const [changing, setChanging] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between gap-2 text-[13px]">
        <span className="text-default">Goes back</span>
        <span className="flex items-center gap-2">
          <b className="text-gray-900">{refundPointLabel[value]}</b>
          <button
            type="button"
            disabled={disabled}
            aria-expanded={changing}
            onClick={() => setChanging((was) => !was)}
            className="text-primary cursor-pointer text-[12.5px] font-semibold"
          >
            {changing ? 'Done' : 'Change'}
          </button>
        </span>
      </div>

      {changing && (
        <div
          role="group"
          aria-label="When the deposit goes back"
          className="mt-2 flex flex-wrap gap-1.5"
        >
          {DepositRefundPointValues.map((option) => (
            <button
              key={option}
              type="button"
              disabled={disabled}
              onClick={() => onChange(option)}
              aria-pressed={option === value}
              className={`h-8 cursor-pointer rounded border px-3 text-[12.5px] ${
                option === value
                  ? 'border-primary bg-primary text-white'
                  : 'border-border-color bg-white text-gray-900 hover:bg-light'
              }`}
            >
              {refundPointLabel[option]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WearerBlock({
  wearer,
  received,
  currency,
  bookingId,
  accessories,
  onToggleAccessory,
  onToggle,
}: {
  wearer: BookingWearer;
  received: ReadonlySet<number>;
  currency: string;
  bookingId: number;
  accessories: ReadonlySet<number>;
  onToggleAccessory: (accessoryId: number) => void;
  onToggle: (garmentId: number) => void;
}) {
  const owed = wearer.garments.filter((garment) => isOutOnRent(garment.state));
  const charges = wearer.garments.reduce((sum, garment) => sum + (garment.damageCharge ?? 0), 0);

  if (owed.length === 0) {
    return null;
  }

  return (
    <section>
      <header className="bg-light flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2.5">
        <span className="text-[13px] font-semibold text-gray-900">{wearer.name}</span>
        {wearer.relation && <span className="text-default text-[11.5px]">{wearer.relation}</span>}
        <span className="ms-auto flex items-center gap-2.5">
          {charges > 0 ? (
            <StatusBadge tone="negative">
              {currency}
              {formatAmount(charges)} charges
            </StatusBadge>
          ) : (
            <StatusBadge tone="positive">No charges</StatusBadge>
          )}
        </span>
      </header>

      <ul className="divide-border-color divide-y">
        {owed.map((garment) => (
          <GarmentRow
            key={garment.id}
            garment={garment}
            checked={received.has(garment.id)}
            currency={currency}
            bookingId={bookingId}
            accessories={accessories}
            onToggleAccessory={onToggleAccessory}
            onToggle={() => onToggle(garment.id)}
          />
        ))}
      </ul>
    </section>
  );
}

function GarmentRow({
  garment,
  checked,
  currency,
  bookingId,
  accessories,
  onToggleAccessory,
  onToggle,
}: {
  garment: BookingGarment;
  checked: boolean;
  currency: string;
  bookingId: number;
  accessories: ReadonlySet<number>;
  onToggleAccessory: (accessoryId: number) => void;
  onToggle: () => void;
}) {
  // Only what the issue counter recorded going out can come back — an accessory that never left
  // cannot be missing, and charging for one is the fastest way to lose a dispute.
  const issued = garment.accessories.filter((accessory) => accessory.issued);
  const missing = issued.filter((accessory) => !accessories.has(accessory.id));

  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-1 px-4 py-2.5 ps-8">
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        aria-label={`Received ${garment.serialCode}`}
        className="mt-1"
      />

      {/* What it looked like on the way out — the picture the clerk is checking this garment
          against, which is the whole point of having taken one. */}
      <GarmentImage url={garment.imageUrl} alt={garment.serialCode} size="sm" />

      <span className="min-w-0 flex-1">
        <span className="text-[13px] font-medium text-gray-900">{garment.itemName}</span>
        <span className="text-default block font-mono text-[11px]">
          {withReference(garment.serialCode, garment.referenceNumber)}
        </span>
        {/* Checked back against what the issue screen recorded going out — which is where the
            "missing accessories" deduction gets its source rather than being invented here. Ticked
            rather than printed: this list used to be read-only, so nothing could ever mark one as
            missing and the deduction had no way to fire. Untick what did not come back. */}
        {issued.length > 0 && (
          <span className="mt-1 block">
            <span className="flex flex-wrap gap-x-3 gap-y-1">
              {issued.map((accessory) => (
                <label
                  key={accessory.id}
                  className="flex cursor-pointer items-center gap-1.5 text-[11.5px] text-gray-900"
                >
                  <input
                    type="checkbox"
                    checked={accessories.has(accessory.id)}
                    onChange={() => onToggleAccessory(accessory.id)}
                  />
                  {accessory.name}
                  {accessory.quantity > 1 && ` ×${accessory.quantity}`}
                </label>
              ))}
            </span>

            <span className="mt-0.5 block text-[11.5px]">
              {missing.length === 0 ? (
                <span className="text-success">All {issued.length} accessories back</span>
              ) : (
                <span className="text-danger">
                  Missing: {missing.map((accessory) => accessory.name).join(', ')}
                </span>
              )}
            </span>
          </span>
        )}
      </span>

      {/* The other half of the evidence pair. A return photograph is only worth anything beside an
          issue photograph, which is why both counters carry the same control. */}
      <ConditionPhotos
        bookingId={bookingId}
        garmentId={garment.id}
        serialCode={garment.serialCode}
        phase="In"
      />

      <span className="flex flex-col items-end gap-1">
        <StatusBadge tone={garment.damageCharge ? 'warning' : 'positive'}>
          {garment.conditionIn ?? 'Clean'}
        </StatusBadge>
        {garment.damageCharge ? (
          <span className="font-mono-nums text-danger text-[12px]">
            {currency}
            {formatAmount(garment.damageCharge)}
          </span>
        ) : null}
      </span>
    </li>
  );
}
